import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

const INFINITE_DEFER_ATTRIBUTE = 'data-infinite-defer-init';

function scheduleAfterAppend(callback) {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();

    if (this.closest(`[${INFINITE_DEFER_ATTRIBUTE}]`)) {
      scheduleAfterAppend(() => {
        if (this.isConnected) this.#connectVariantPicker();
      });
      return;
    }

    this.#connectVariantPicker();
  }

  #connectVariantPicker() {
    const shouldSkipInitialSizeSelection = this.#shouldSkipInitialSizeSelection();

    this.#initializeSelectionState();

    if (shouldSkipInitialSizeSelection) {
      this.#clearSizeSelection();
      this.#markInitialSizeSelectionSkipped();
      this.#initializeSelectionState();
    }

    this.#syncProductPageUrl();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initializeSelectionState();
  }

  #initializeSelectionState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#checkedIndices = [];
    this.#radios = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const checkedRadio = radios.find((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      const checkedIndex = checkedRadio ? radios.indexOf(checkedRadio) : -1;

      this.#checkedIndices.push(checkedIndex !== -1 ? [checkedIndex] : []);
    });
  }

  /**
   * Determine whether size should start unselected on initial PDP load.
   * @returns {boolean}
   */
  #shouldSkipInitialSizeSelection() {
    if (this.dataset.templateProductMatch !== 'true') return false;
    const productForm = this.#getProductSelectorScope()?.querySelector('product-form-component');
    if (!(productForm instanceof HTMLElement) || productForm.dataset.requireSizeSelection !== 'true') return false;
    if (this.#hasInitialSizeSelectionBeenSkipped()) return false;

    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    if (sizeFieldset) {
      return sizeFieldset.querySelectorAll('input[data-option-value-id]').length > 1;
    }

    const sizeSelect = this.querySelector('select[data-option-name="size"]');
    if (sizeSelect instanceof HTMLSelectElement) {
      const selectableOptions = Array.from(sizeSelect.options).filter((option) => option.dataset.optionValueId);
      return selectableOptions.length > 1;
    }

    return false;
  }

  #getSizeSkipKey() {
    return `size-skip:${this.dataset.productId || this.dataset.productUrl || window.location.pathname}`;
  }

  #hasInitialSizeSelectionBeenSkipped() {
    const registry = window.__darveysSizeSkipApplied;
    if (!(registry instanceof Set)) return false;
    return registry.has(this.#getSizeSkipKey());
  }

  #markInitialSizeSelectionSkipped() {
    if (!(window.__darveysSizeSkipApplied instanceof Set)) {
      window.__darveysSizeSkipApplied = new Set();
    }
    window.__darveysSizeSkipApplied.add(this.#getSizeSkipKey());
  }

  /**
   * Clears checked/current/previous state for all radios in a fieldset.
   * @param {HTMLInputElement[]} radios
   */
  #clearFieldsetSelection(radios) {
    for (const radio of radios) {
      radio.checked = false;
      radio.dataset.currentChecked = 'false';
      radio.dataset.previousChecked = 'false';
    }
  }

  /**
   * Injects and selects a placeholder option so dropdown size starts unselected.
   */
  #resetSizeDropdownSelection() {
    const sizeSelect = this.querySelector('select[data-option-name="size"]');
    if (!(sizeSelect instanceof HTMLSelectElement)) return;

    const selectableOptions = Array.from(sizeSelect.options).filter((option) => option.dataset.optionValueId);
    if (selectableOptions.length <= 1) return;

    let placeholder = Array.from(sizeSelect.options).find(
      (option) => option.value === '' && !option.dataset.optionValueId
    );

    if (!placeholder) {
      placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.textContent = 'Select Size';
      sizeSelect.insertBefore(placeholder, sizeSelect.firstChild);
    }

    for (const option of sizeSelect.options) {
      option.removeAttribute('selected');
    }

    placeholder.selected = true;
    placeholder.setAttribute('selected', 'selected');
    sizeSelect.value = '';
  }

  #clearSizeSelection() {
    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    if (sizeFieldset instanceof HTMLFieldSetElement) {
      const radios = Array.from(sizeFieldset.querySelectorAll('input'));
      if (radios.length > 1) {
        this.#clearFieldsetSelection(radios);
      }
    }

    this.#resetSizeDropdownSelection();
  }

  #hasSizeSelection() {
    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    if (sizeFieldset instanceof HTMLFieldSetElement) {
      return Boolean(sizeFieldset.querySelector('input:checked'));
    }

    const sizeSelect = this.querySelector('select[data-option-name="size"]');
    if (sizeSelect instanceof HTMLSelectElement) {
      const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
      return sizeSelect.value !== '' && !selectedOption?.disabled;
    }

    return false;
  }

  #isSizeSelectionRequired() {
    const productForm = this.#getProductSelectorScope()?.querySelector('product-form-component');
    return productForm instanceof HTMLElement && productForm.dataset.requireSizeSelection === 'true';
  }

  #getProductSelectorScope() {
    const scopedContainer = this.closest('[data-product-selector]');
    if (scopedContainer) return scopedContainer;

    const parentContainer = this.parentElement;
    if (parentContainer?.querySelector('product-form-component')) return parentContainer;

    return this.closest('#quick-add-modal-content, .quick-add-modal__content, quick-add-dialog');
  }

  #normalizeSizeSelectionAfterRender(hadSizeSelection) {
    if (!this.#isSizeSelectionRequired()) return;
    if (hadSizeSelection) return;

    this.#clearSizeSelection();
    this.#initializeSelectionState();
  }

  #syncProductPageUrl(pathname) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' && !this.closest('product-card') && !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const url = new URL(window.location.href);

    if (pathname) {
      url.pathname = pathname;
    }

    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
 variantChanged(event) {
  if (!(event.target instanceof HTMLElement)) return;

  const selectedOption =
    event.target instanceof HTMLSelectElement
      ? event.target.options[event.target.selectedIndex]
      : event.target;

  if (!selectedOption) return;

const cleanedMpn = document.getElementById('cleaned_mpn');
const mpnSize = document.getElementById('mpn_size');
const darveysSize = document.getElementById('darveys_size');
const darveysSizeUnit = document.getElementById('darveys_size_unit');
const boutiqueSize = document.getElementById('boutique_size');
const boutiqueSizeUnit = document.getElementById('boutique_size_unit');

if (cleanedMpn) {
  cleanedMpn.value = selectedOption.dataset.mpnCleaned || '';
}

if (mpnSize) {
  mpnSize.value = selectedOption.dataset.mpnSize || '';
}

if (darveysSize) {
  darveysSize.value = selectedOption.dataset.darveysSize || '';
}

if (darveysSizeUnit) {
  darveysSizeUnit.value = selectedOption.dataset.darveysSizeUnit || '';
}

if (boutiqueSize) {
  boutiqueSize.value = selectedOption.dataset.boutiqueSize || '';
}

if (boutiqueSizeUnit) {
  boutiqueSizeUnit.value = selectedOption.dataset.boutiqueSizeUnit || '';
}

  this.updateSelectedOption(event.target);

  this.dispatchEvent(
    new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
      variantId: selectedOption.dataset.variantId ?? '',
    })
  );

  const isOnProductPage =
    this.dataset.templateProductMatch === 'true' &&
    !event.target.closest('product-card') &&
    !event.target.closest('quick-add-dialog');

  const currentUrl = this.dataset.productUrl?.split('?')[0];
  const newUrl = selectedOption.dataset.connectedProductUrl;
  const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
  const isOnFeaturedProductSection = Boolean(
    this.closest('featured-product-information')
  );

  const morphElementSelector = loadsNewProduct
    ? 'main'
    : isOnFeaturedProductSection
    ? 'featured-product-information'
    : undefined;

  this.fetchUpdatedSection(
    this.buildRequestUrl(selectedOption),
    morphElementSelector
  );

  this.#syncProductPageUrl(loadsNewProduct ? newUrl : undefined);
}

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (!source) {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
      } else if (selectedOption.dataset.optionValueId) {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;
    const hadSizeSelection = this.#hasSizeSelection();

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#normalizeSizeSelectionAfterRender(hadSizeSelection);

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected][data-option-value-id], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) return undefined;
    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected][data-option-value-id], fieldset input:checked')
    );

    return selectedOptions
      .map((option) => option.dataset.optionValueId)
      .filter((optionValueId) => Boolean(optionValueId));
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
